import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf, ChevronDown } from 'lucide-react';
import { menuData, menuImages } from '../data/menuData';

export const Menu = () => {
  const [activeCategory, setActiveCategory] = useState(menuData.categories[0].id);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Get image for menu item
  const getItemImage = (itemName) => {
    return menuImages[itemName] || null;
  };

  // Get current category data
  const currentCategory = menuData.categories.find(cat => cat.id === activeCategory) || menuData.categories[0];

  const handleCategorySelect = (categoryId) => {
    setActiveCategory(categoryId);
    setIsDropdownOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="menu-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 px-6 md:px-12 bg-white" data-testid="menu-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Our Menu</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1917] mt-4 mb-6">
              Authentic Italian Delights
            </h1>
            <p className="text-stone-500 text-lg md:text-xl leading-relaxed">
              From freshly baked pastries to handcrafted pasta, every dish is made with love and traditional Italian recipes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Menu Content */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="menu-content">
        <div className="max-w-7xl mx-auto">
          {/* Category Tabs - Desktop */}
          <div className="hidden md:block mb-12 sticky top-20 z-30 bg-[#FDFBF7] py-4 -mx-6 px-6">
            <div className="flex flex-wrap justify-center gap-2">
              {menuData.categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-3 text-sm font-medium uppercase tracking-wider transition-all rounded-sm border ${
                    activeCategory === category.id
                      ? 'text-[#3A4D39] bg-white shadow-sm border-stone-200'
                      : 'text-stone-500 border-transparent hover:text-[#3A4D39]'
                  }`}
                  data-testid={`menu-tab-${category.id}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Category Select - Mobile (Custom Dropdown) */}
          <div className="md:hidden mb-8 relative" data-testid="menu-mobile-dropdown">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full bg-white border border-stone-200 px-4 py-3 text-[#1C1917] font-medium flex items-center justify-between rounded-sm"
              data-testid="menu-dropdown-trigger"
            >
              <span>{currentCategory.name}</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-sm shadow-lg z-50 max-h-80 overflow-y-auto"
                >
                  {menuData.categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category.id)}
                      className={`w-full px-4 py-3 text-left transition-colors ${
                        activeCategory === category.id
                          ? 'bg-[#3A4D39] text-white'
                          : 'text-[#1C1917] hover:bg-stone-100'
                      }`}
                      data-testid={`menu-option-${category.id}`}
                    >
                      {category.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Click outside to close dropdown */}
          {isDropdownOpen && (
            <div 
              className="fixed inset-0 z-40 md:hidden" 
              onClick={() => setIsDropdownOpen(false)}
            />
          )}

          {/* Menu Items */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-8">
              <h2 className="font-serif text-3xl font-bold text-[#1C1917]">{currentCategory.name}</h2>
              <p className="text-stone-500 mt-2">{currentCategory.items.length} items</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {currentCategory.items.map((item, index) => {
                const itemImage = getItemImage(item.name);
                
                return (
                  <motion.div
                    key={item.name}
                    className="bg-white border border-stone-100 hover:border-stone-300 transition-all duration-300 group overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    data-testid={`menu-item-${currentCategory.id}-${index}`}
                  >
                    <div className="flex flex-col sm:flex-row">
                      {/* Image */}
                      {itemImage && (
                        <div className="sm:w-40 md:w-48 flex-shrink-0">
                          <div className="aspect-square sm:aspect-[4/3] md:aspect-square overflow-hidden">
                            <img
                              src={itemImage.src}
                              alt={itemImage.alt}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className={`p-6 flex-1 flex flex-col justify-between ${!itemImage ? 'w-full' : ''}`}>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <h3 className="font-serif text-xl font-bold text-[#1C1917] group-hover:text-[#3A4D39] transition-colors">
                              {item.name}
                            </h3>
                            {item.tags?.includes('Vegan') && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                <Leaf className="w-3 h-3" />
                                Vegan
                              </span>
                            )}
                          </div>
                          <p className="text-stone-500 text-sm leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                        <p className="text-[#3A4D39] font-bold text-lg mt-4">
                          {item.price}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#3A4D39]" data-testid="menu-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
            Come Visit Us
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Enjoy our full menu at the café. 110 Haydons Road, Wimbledon.
          </p>
        </div>
      </section>
    </div>
  );
};
